/**
 * Settings Page
 *
 * Allows users to configure application preferences including notifications,
 * display preferences, and data management options.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft,
  Bell,
  Moon,
  Globe,
  Download,
  Trash2,
  HelpCircle,
  ExternalLink,
  Check,
  AlertCircle,
} from 'lucide-react';

interface NotificationSettings {
  emailDeadlines: boolean;
  emailCareLogReminders: boolean;
  emailWeeklySummary: boolean;
  pushDeadlines: boolean;
  pushCareLogReminders: boolean;
}

interface DisplaySettings {
  theme: 'light' | 'dark' | 'system';
  dateFormat: 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD';
  timeFormat: '12h' | '24h';
}

export function Settings(): JSX.Element {
  const navigate = useNavigate();
  const { signOut } = useAuth();

  // Notification settings state
  const [notifications, setNotifications] = useState<NotificationSettings>({
    emailDeadlines: true,
    emailCareLogReminders: true,
    emailWeeklySummary: false,
    pushDeadlines: true,
    pushCareLogReminders: false,
  });

  // Display settings state
  const [display, setDisplay] = useState<DisplaySettings>({
    theme: 'system',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h',
  });

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleNotificationChange = (key: keyof NotificationSettings) => {
    setNotifications((prev) => ({ ...prev, [key]: !prev[key] }));
    // In a real app, you would save this to the backend
    setSuccess('Notification preferences updated');
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleDisplayChange = <K extends keyof DisplaySettings>(
    key: K,
    value: DisplaySettings[K]
  ) => {
    setDisplay((prev) => ({ ...prev, [key]: value }));
    // In a real app, you would save this to the backend/localStorage
    setSuccess('Display preferences updated');
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleExportData = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Placeholder for data export functionality
      // In a real app, this would call a Cloud Function to generate an export
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setSuccess('Your data export has been initiated. You will receive an email with the download link.');
    } catch (err) {
      console.error('Error exporting data:', err);
      setError('Failed to export data. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Placeholder for account deletion
      // In a real app, this would call a Cloud Function to delete all user data
      await new Promise((resolve) => setTimeout(resolve, 1500));
      await signOut();
      navigate('/');
    } catch (err) {
      console.error('Error deleting account:', err);
      setError('Failed to delete account. Please try again or contact support.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-airy">
      <header className="bg-white border-b border-soft-neutral">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <h1 className="text-2xl font-display text-deep-forest font-light">Hesvara</h1>
          <button onClick={() => navigate('/')} className="btn-ghost btn-sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </button>
        </div>
      </header>

      <main id="main-content" tabIndex={-1} className="max-w-2xl mx-auto px-4 py-8 sm:px-6 lg:px-8 outline-none">
        <div className="mb-8">
          <h2 className="text-2xl font-display text-deep-forest mb-2">Settings</h2>
          <p className="text-muted">Customize your Hesvara experience</p>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="alert alert-success mb-6 flex items-center gap-2">
            <Check className="w-5 h-5" />
            {success}
          </div>
        )}
        {error && (
          <div className="alert alert-error mb-6 flex items-center gap-2" role="alert">
            <AlertCircle className="w-5 h-5" aria-hidden="true" />
            {error}
          </div>
        )}

        {/* Notification Settings */}
        <div className="card mb-6">
          <div className="card-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-evergreen/10 rounded-full flex items-center justify-center">
                <Bell className="w-5 h-5 text-evergreen" />
              </div>
              <div>
                <h3 className="font-semibold text-ink">Notifications</h3>
                <p className="text-sm text-muted">Manage how you receive alerts</p>
              </div>
            </div>
          </div>
          <div className="card-body space-y-4">
            <div className="border-b border-soft-neutral pb-4">
              <h4 className="text-sm font-medium text-ink mb-3">Email Notifications</h4>
              <div className="space-y-3">
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm text-ink">Deadline reminders</span>
                  <input
                    type="checkbox"
                    checked={notifications.emailDeadlines}
                    onChange={() => handleNotificationChange('emailDeadlines')}
                    className="w-5 h-5 rounded border-soft-neutral text-evergreen focus:ring-evergreen"
                  />
                </label>
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm text-ink">Care log reminders</span>
                  <input
                    type="checkbox"
                    checked={notifications.emailCareLogReminders}
                    onChange={() => handleNotificationChange('emailCareLogReminders')}
                    className="w-5 h-5 rounded border-soft-neutral text-evergreen focus:ring-evergreen"
                  />
                </label>
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm text-ink">Weekly summary</span>
                  <input
                    type="checkbox"
                    checked={notifications.emailWeeklySummary}
                    onChange={() => handleNotificationChange('emailWeeklySummary')}
                    className="w-5 h-5 rounded border-soft-neutral text-evergreen focus:ring-evergreen"
                  />
                </label>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-ink mb-3">Push Notifications</h4>
              <div className="space-y-3">
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm text-ink">Deadline alerts</span>
                  <input
                    type="checkbox"
                    checked={notifications.pushDeadlines}
                    onChange={() => handleNotificationChange('pushDeadlines')}
                    className="w-5 h-5 rounded border-soft-neutral text-evergreen focus:ring-evergreen"
                  />
                </label>
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm text-ink">Care log reminders</span>
                  <input
                    type="checkbox"
                    checked={notifications.pushCareLogReminders}
                    onChange={() => handleNotificationChange('pushCareLogReminders')}
                    className="w-5 h-5 rounded border-soft-neutral text-evergreen focus:ring-evergreen"
                  />
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Display Settings */}
        <div className="card mb-6">
          <div className="card-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-evergreen/10 rounded-full flex items-center justify-center">
                <Moon className="w-5 h-5 text-evergreen" />
              </div>
              <div>
                <h3 className="font-semibold text-ink">Display</h3>
                <p className="text-sm text-muted">Customize your viewing experience</p>
              </div>
            </div>
          </div>
          <div className="card-body space-y-4">
            <div>
              <label className="label">Theme</label>
              <select
                value={display.theme}
                onChange={(e) => handleDisplayChange('theme', e.target.value as DisplaySettings['theme'])}
                className="input"
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
                <option value="system">System default</option>
              </select>
            </div>

            <div>
              <label className="label">Date Format</label>
              <select
                value={display.dateFormat}
                onChange={(e) => handleDisplayChange('dateFormat', e.target.value as DisplaySettings['dateFormat'])}
                className="input"
              >
                <option value="MM/DD/YYYY">MM/DD/YYYY (US)</option>
                <option value="DD/MM/YYYY">DD/MM/YYYY (International)</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD (ISO)</option>
              </select>
            </div>

            <div>
              <label className="label">Time Format</label>
              <select
                value={display.timeFormat}
                onChange={(e) => handleDisplayChange('timeFormat', e.target.value as DisplaySettings['timeFormat'])}
                className="input"
              >
                <option value="12h">12-hour (1:00 PM)</option>
                <option value="24h">24-hour (13:00)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Language (Placeholder) */}
        <div className="card mb-6">
          <div className="card-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-evergreen/10 rounded-full flex items-center justify-center">
                <Globe className="w-5 h-5 text-evergreen" />
              </div>
              <div>
                <h3 className="font-semibold text-ink">Language</h3>
                <p className="text-sm text-muted">Choose your preferred language</p>
              </div>
            </div>
          </div>
          <div className="card-body">
            <select className="input" defaultValue="en-US">
              <option value="en-US">English (US)</option>
              <option value="es" disabled>Spanish (Coming soon)</option>
              <option value="fr" disabled>French (Coming soon)</option>
            </select>
          </div>
        </div>

        {/* Data Management */}
        <div className="card mb-6">
          <div className="card-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-evergreen/10 rounded-full flex items-center justify-center">
                <Download className="w-5 h-5 text-evergreen" />
              </div>
              <div>
                <h3 className="font-semibold text-ink">Data Management</h3>
                <p className="text-sm text-muted">Export or manage your data</p>
              </div>
            </div>
          </div>
          <div className="card-body space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-ink">Export your data</p>
                <p className="text-xs text-muted">Download a copy of all your data</p>
              </div>
              <button
                onClick={handleExportData}
                disabled={isSubmitting}
                className="btn-secondary btn-sm"
              >
                {isSubmitting ? 'Exporting...' : 'Export'}
              </button>
            </div>
          </div>
        </div>

        {/* Help & Support */}
        <div className="card mb-6">
          <div className="card-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-evergreen/10 rounded-full flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-evergreen" />
              </div>
              <div>
                <h3 className="font-semibold text-ink">Help & Support</h3>
                <p className="text-sm text-muted">Get help with Hesvara</p>
              </div>
            </div>
          </div>
          <div className="card-body space-y-3">
            <a
              href="https://hesvara.com/help"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between py-2 text-sm text-ink hover:text-evergreen transition-colors"
            >
              <span>Help Center</span>
              <ExternalLink className="w-4 h-4" />
            </a>
            <a
              href="https://hesvara.com/contact"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between py-2 text-sm text-ink hover:text-evergreen transition-colors"
            >
              <span>Contact Support</span>
              <ExternalLink className="w-4 h-4" />
            </a>
            <a
              href="https://hesvara.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between py-2 text-sm text-ink hover:text-evergreen transition-colors"
            >
              <span>Privacy Policy</span>
              <ExternalLink className="w-4 h-4" />
            </a>
            <a
              href="https://hesvara.com/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between py-2 text-sm text-ink hover:text-evergreen transition-colors"
            >
              <span>Terms of Service</span>
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="card border-red-200">
          <div className="card-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-red-600">Danger Zone</h3>
                <p className="text-sm text-muted">Irreversible account actions</p>
              </div>
            </div>
          </div>
          <div className="card-body">
            {!showDeleteConfirm ? (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-ink">Delete your account</p>
                  <p className="text-xs text-muted">
                    Permanently remove your account and all associated data
                  </p>
                </div>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="btn-danger btn-sm"
                >
                  Delete Account
                </button>
              </div>
            ) : (
              <div className="bg-red-50 rounded-lg p-4">
                <p className="text-sm text-red-800 mb-4">
                  Are you sure you want to delete your account? This action cannot be undone.
                  All your CaseFiles, documents, care logs, and other data will be permanently deleted.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={isSubmitting}
                    className="btn-secondary btn-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={isSubmitting}
                    className="btn-danger btn-sm"
                  >
                    {isSubmitting ? 'Deleting...' : 'Yes, Delete My Account'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* App Version */}
        <div className="text-center mt-8 text-sm text-muted">
          <p>Hesvara v1.0.0</p>
          <p className="mt-1">Made with care for caregivers</p>
        </div>
      </main>
    </div>
  );
}

export default Settings;
